import { ref } from 'vue';

import { useSocketMessageHandler } from '@/composables/domains/chat/socket/useSocketMessageHandler';
import { useChatStore } from '@/stores/chat';
import { UseChatMessagesOptions } from '@/types/composables';
import { ChatMessage as LegacyChatMessage, MessageMap, MessageText } from '@/types/socket';
import { InboundMessageCN } from '@/types/socket/inbound';
import {
  ChatMessage,
  ChatMessageBody,
  ChatMessageContent,
  MessageCategory,
  MessagePosition,
} from '@/types/socket/message';
import { getChatMessageHandlers } from '@/utils/constants/socket';
import { base64UrlSafeToString } from '@/utils/encryptor';

let instance = null as ReturnType<typeof useMessagesCall> | null; // 싱글톤 인스턴스

export function useMessagesCall(options?: UseChatMessagesOptions) {
  // CallContents, socketStore 에서 공용되기 때문에 싱글톤 패턴 강제
  if (!instance) {
    if (!options) {
      throw new Error('useMessagesCall must be initialized with options before use.');
    }
    instance = createMessagesCallInstance(options);
  }
  return instance;
}

// 인스턴스 생성자
function createMessagesCallInstance({ selectedChatId }: UseChatMessagesOptions) {
  const chatStore = useChatStore();

  const prevMyMessageId = ref('');
  const prevOtherMessageId = ref('');
  const emptyBubbleCallLogIdMy = ref('');
  const emptyBubbleCallLogIdOther = ref('');

  const messageMap = ref<MessageMap>({}); // 수신된 메세지 모듬 정제 1단계
  const messageList = ref<Array<LegacyChatMessage>>([]); // 수신된 메세지 모듬 정제 2단계

  // FIXME: 소켓리팩 2차 개선때, 삭제필요 (message Store의 상태값으로만 유지되어야합니다.) (26/1/26)
  const handleWebSocketMessage = async (data) => {
    const { handleSocketMessage } = useSocketMessageHandler(getChatMessageHandlers());
    await handleSocketMessage(data);
  };

  const convertCategoryToFlag = (category: MessageCategory) => {
    return (
      {
        MESSAGE: 'tr',
        DIRECT_MESSAGE: 'mt',
        DATE_MESSAGE: 'date',
        ENTER_MESSAGE: 'in',
        EXIT_MESSAGE: 'out',
      }[category] || 'tr'
    );
  };

  const convertMessageContent = (contents: ChatMessageContent[]): MessageText[] => {
    return contents.reduce((acc: MessageText[], content) => {
      const message: MessageText = {
        chatSeqSubId: content.chatSubId,
        subId: content.chatSubId,
        languageCode: content.languageCode,
        isTextComplete: ['FIXED', 'SENTENCE_COMPLETED'].includes(content.status),
        isThoroughlyComplete: 'SENTENCE_COMPLETED' === content.status,
        fadeout: false,
        highlight: false,
        isSourcePlaying: false,
        isEditable: 'SENTENCE_COMPLETED' === content.status,
        key: 0,
        logFlag: '',
        content: '',
        messageEdited: '',
      };
      // 01. completeText
      if (content.completeText) {
        acc.push({ ...message, content: content.completeText, highlight: false });
      }
      // 02. incompleteText
      if (content.incompleteText) {
        acc.push({ ...message, content: content.incompleteText, highlight: true });
      }
      return acc;
    }, []);
  };

  const convertMessageBody = (msg: ChatMessageBody) => {
    return {
      contents: convertMessageContent(msg.contents),
      isComplete: msg.contents.every(({ status }) => status === 'SENTENCE_COMPLETED'),
      isAudioComplete: msg.audios.length > 0,
    };
  };

  const updateLegacyMessageNickname = (msg: InboundMessageCN) => {
    messageList.value.forEach((message) => {
      if (message.senderId === msg.userId) {
        message.nickname = base64UrlSafeToString(msg.nickname);
      }
    });
  };

  const upsertLegacyMessage = (msg: ChatMessage, position: MessagePosition = 'append') => {
    const message: LegacyChatMessage = {
      callLogId: msg.chatLogId,
      flag: convertCategoryToFlag(msg.category),
      inout: convertCategoryToFlag(msg.category),
      src: convertMessageBody(msg.src),
      tgt: convertMessageBody(msg.tgt),
      srcAudioKeys: msg.src.audios.map((audio) => ({ chatId: audio.chatId, voiceKey: audio.voiceKey })),
      tgtAudioKeys: msg.tgt.audios.map((audio) => ({ chatId: audio.chatId, voiceKey: audio.voiceKey })),
      srcLanguageSet: new Set(msg.src.languageCodes),
      tgtLanguageSet: new Set(msg.tgt.languageCodes),
      isAudioComplete: msg.src.audios.length > 0 || msg.tgt.audios.length > 0,
      isOwn: msg.isOwn,
      senderId: msg.senderId,
      nickname: msg.nickname,
      sentAt: msg.sentAt,
      autoPlay: true,
      reset: '1',
      state: 'SUCCESS',
    };

    const index = messageList.value.findIndex(({ callLogId }) =>
      [msg.chatLogId, msg.pastChatLogId].includes(callLogId)
    );
    if (index === -1) {
      if (position === 'prepend') {
        messageList.value.unshift(message);
      } else {
        messageList.value.push(message);
      }
    } else {
      messageList.value[index] = message;
    }
  };

  const deleteMessageLegacy = (chatLogId: string) => {
    const inx = messageList.value.findIndex(({ callLogId }) => callLogId === chatLogId);

    if (inx !== 0) {
      messageList.value.splice(inx, 1);
      delete messageMap.value[chatLogId];
      chatStore.deleteErrorChat(selectedChatId.value, chatLogId);
    }
  };

  // 빈 버블 삭제 (나의 발화)
  const setEmptyBubbleMy = (chatLogId: string) => {
    emptyBubbleCallLogIdMy.value = chatLogId;
  };

  // 빈 버블 삭제 (상대 발화)
  const setEmptyBubbleOther = (chatLogId: string) => {
    emptyBubbleCallLogIdOther.value = chatLogId;
  };

  // 빈 챗 버블 없애기
  const popBubbleEmpty = (chatLogId: string) => {
    const popTargetIndex = messageList.value.findIndex((message) => message.callLogId === chatLogId);
    messageList.value.splice(popTargetIndex, 1);
    messageList.value = [...messageList.value]; // DOM update 강제

    if (emptyBubbleCallLogIdMy.value === chatLogId) {
      emptyBubbleCallLogIdMy.value = '';
    } else {
      emptyBubbleCallLogIdOther.value = '';
    }
  };

  // 이 composable 초기화
  const resetUseMessagesCall = () => {
    messageMap.value = {};
    messageList.value = [];
    prevMyMessageId.value = '';
    prevOtherMessageId.value = '';
    emptyBubbleCallLogIdMy.value = '';
    emptyBubbleCallLogIdOther.value = '';
  };

  return {
    messageMap,
    messageList,
    prevMyMessageId,
    prevOtherMessageId,
    emptyBubbleCallLogIdMy,
    emptyBubbleCallLogIdOther,
    handleWebSocketMessage,
    popBubbleEmpty,
    setEmptyBubbleMy,
    setEmptyBubbleOther,
    resetUseMessagesCall,
    upsertLegacyMessage,
    deleteMessageLegacy,
    updateLegacyMessageNickname,
  };
}
